/**
 * MobileTopBannerSticky - Enhanced wrapper for sticky mobile banner
 * 
 * Provides:
 * - Sticky positioning at top of viewport
 * - Proper z-index management
 * - Height calculation for message list offset
 * - Smooth transitions during keyboard events
 */
import React, { useRef, useEffect, useState } from 'react';
import { useMobileViewport } from '../../hooks/useMobileViewport';

export const MOBILE_BANNER_HEIGHT = 56; // Standard height for mobile banner

/**
 * Hook to manage sticky banner behavior
 */
export const useStickyBanner = () => {
  const [bannerHeight, setBannerHeight] = useState(MOBILE_BANNER_HEIGHT);
  const bannerRef = useRef(null);
  
  const {
    safeArea,
    isKeyboardVisible,
    Z_INDEX
  } = useMobileViewport();
  
  // Measure actual banner height
  useEffect(() => {
    if (bannerRef.current) {
      const observer = new ResizeObserver((entries) => {
        for (let entry of entries) {
          setBannerHeight(entry.contentRect.height);
        }
      });
      
      observer.observe(bannerRef.current);
      return () => observer.disconnect();
    }
  }, []);
  
  // Calculate styles for sticky banner
  const getStickyBannerStyles = () => ({
    position: 'fixed',
    top: safeArea.top || 0,
    left: 0,
    right: 0,
    zIndex: Z_INDEX.mobileTopBanner || 120,
    // Add subtle shadow for depth
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    // Ensure it stays above content but below modals
    willChange: 'transform',
    // Smooth transitions
    transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
  });
  
  // Calculate content area offset (for message list)
  const getContentOffset = () => {
    return bannerHeight + safeArea.top;
  };
  
  return {
    bannerRef,
    bannerHeight,
    getStickyBannerStyles,
    getContentOffset,
    isKeyboardVisible
  };
};

/**
 * Wrapper component for making any banner sticky
 */
export const StickyBannerWrapper = ({ children, className = '' }) => {
  const {
    bannerRef,
    getStickyBannerStyles
  } = useStickyBanner();
  
  return (
    <div 
      ref={bannerRef}
      className={`mobile-top-banner-sticky ${className}`}
      style={getStickyBannerStyles()}
    >
      {children}
    </div>
  );
};

/**
 * Spacer component to push content below sticky banner
 */
export const BannerSpacer = () => {
  const { getContentOffset } = useStickyBanner();
  
  return (
    <div 
      className="banner-spacer"
      style={{ height: getContentOffset() }}
      aria-hidden="true"
    />
  );
};

export default {
  useStickyBanner,
  StickyBannerWrapper,
  BannerSpacer,
  MOBILE_BANNER_HEIGHT
};